import { useState } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { BtnPrimary, Button } from '@components/buttons';
import { FormGroup, InputLabel, AsyncForm } from '@components/async-form';
import { Password } from '@components/password';
import { IcoLock } from '@components/icons';

type Props = {
  title?: string;
  subtitle?: string;
  onComplete(): Promise<void>;
  onSkip(): Promise<void>;
};

export function SetPasswordForm({ title, subtitle, onComplete, onSkip }: Props) {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  return (
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-bold">{title || intl('Set Your Password')}</h3>
        <p class="text-gray-600">
          {subtitle ||
            intl(
              'Create a password so you can log in later. You can also skip this step and set a password anytime using "Forgot password".',
            )}
        </p>
      </div>

      <AsyncForm
        class="flex flex-col gap-4"
        onSubmit={async (data: any) => {
          try {
            setIsLoading(true);
            await rpx.auth.setInitialPassword({
              newPassword: data.newPassword,
              confirmPassword: data.confirmPassword,
            });
            await onComplete();
          } catch (err) {
            showError(err);
          } finally {
            setIsLoading(false);
          }
        }}
      >
        <FormGroup prop="newPassword">
          <InputLabel>{intl('Password')}</InputLabel>
          <label class="relative flex">
            <span class="absolute flex items-center justify-center left-px top-px bottom-px w-10 bg-gray-50 rounded-l border-r">
              <IcoLock />
            </span>
            <Password
              name="newPassword"
              placeholder={intl('Choose a password')}
              autocomplete="new-password"
              autoFocus
              wrapperClass="w-full"
              class="bg-transparent pl-14"
            />
          </label>
        </FormGroup>

        <FormGroup prop="confirmPassword">
          <InputLabel>{intl('Confirm Password')}</InputLabel>
          <label class="relative flex">
            <span class="absolute flex items-center justify-center left-px top-px bottom-px w-10 bg-gray-50 rounded-l border-r">
              <IcoLock />
            </span>
            <Password
              name="confirmPassword"
              placeholder={intl('Confirm your password')}
              autocomplete="new-password"
              wrapperClass="w-full"
              class="bg-transparent pl-14"
            />
          </label>
        </FormGroup>

        <div class="flex flex-col gap-3">
          <BtnPrimary class="rounded-md py-3 font-semibold w-full" isLoading={isLoading}>
            {intl('Set Password & Continue →')}
          </BtnPrimary>
          <Button
            class="text-gray-500 hover:text-gray-700 text-sm"
            isLoading={isSkipping}
            onClick={async () => {
              try {
                setIsSkipping(true);
                await onSkip();
              } catch (err) {
                showError(err);
              } finally {
                setIsSkipping(false);
              }
            }}
          >
            {intl('Skip for now →')}
          </Button>
        </div>
      </AsyncForm>
    </div>
  );
}
